import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Edit, Copy, Building2, Mail, RefreshCw, KeyRound } from 'lucide-react';
import { Organizer } from '../../types/organizer';
import { Flag } from '../common/Flag';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';
import { useOrganizersStore } from '../../stores/organizersStore';
import { useConfirm } from '../../hooks/useConfirm';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { EditOrganizerModal } from './EditOrganizerModal';
import { ReplaceAuthModal } from './ReplaceAuthModal';

type Props = {
  organizers: Organizer[];
  onSyncNameClick?: (organizerId: string) => void;
};

export const OrganizersCardView = ({ organizers, onSyncNameClick }: Props) => {
  const [editing, setEditing] = useState<Organizer | null>(null);
  const [replacingAuth, setReplacingAuth] = useState<Organizer | null>(null);
  const [syncingOrganizerId, setSyncingOrganizerId] = useState<string | null>(null);
  const { translations } = useLanguage();
  const { deleteOrganizer } = useOrganizersStore();
  const { isOpen, config, confirm, close } = useConfirm();

  const copyToClipboard = async (text: string, type: 'email') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(
        type === 'email'
          ? translations?.organizerRow?.copyEmailSuccess || 'Email copied'
          : translations?.organizerRow?.copySuccess || 'Copied to clipboard'
      );
    } catch (error) {
      toast.error(translations?.organizerRow?.copyError || 'Failed to copy');
    }
  };

  const handleDelete = async (organizer: Organizer) => {
    try {
      await deleteOrganizer(organizer.id);
      toast.success(translations?.organizersList?.deleteSuccess || 'Organizer deleted successfully');
    } catch (error: any) {
      console.error('Error deleting organizer:', error);
      toast.error(error.message || translations?.organizersList?.deleteError || 'Failed to delete organizer');
    }
  };

  const confirmDelete = (organizer: Organizer) => {
    confirm({
      title: translations?.organizersList?.deleteTitle || 'Delete Organizer',
      message: `${translations?.organizersList?.deleteMessage || 'Are you sure you want to delete the organizer'} "${organizer.name}"?`,
      onConfirm: () => handleDelete(organizer),
      type: 'danger',
    });
  };

  // Get country name from code
  const getCountryNameFromCode = (code: string): string => {
    const countries: Record<string, string> = {
      'CH': 'Switzerland',
      'FR': 'France',
      'IT': 'Italy',
      'CA': 'Canada',
      'ES': 'Spain',
      'CN': 'China',
      'GB': 'UK',
      'NL': 'Netherlands',
      'BE': 'Belgium',
      'US': 'USA',
      'DE': 'Germany',
      'SE': 'Sweden',
      'NZ': 'New Zealand'
    };

    return countries[code] || code;
  };

  // Skip animation for large lists
  const shouldAnimate = organizers.length < 50;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {organizers.map((organizer, index) => (
        <motion.div
          key={organizer.id}
          initial={shouldAnimate ? { opacity: 0, y: 20 } : false}
          animate={shouldAnimate ? { opacity: 1, y: 0 } : false}
          transition={shouldAnimate ? { delay: Math.min(index * 0.05, 1) } : undefined}
          className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow"
        >
          {/* Header */}
          <div className="p-4 border-b bg-gradient-to-r from-primary-50 to-white">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-600 font-semibold text-lg">
                    {organizer.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{organizer.name}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {organizer.country && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-full text-xs font-medium text-gray-600 border border-gray-200">
                        <Flag countryCode={organizer.country} size={14} />
                        <span>{getCountryNameFromCode(organizer.country)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => confirmDelete(organizer)}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                  title={translations?.organizerRow?.delete || 'Delete'}
                >
                  <Trash2 size={16} />
                </button>
                <button
                  onClick={() => setEditing(organizer)}
                  className="p-1.5 text-primary-500 hover:bg-primary-50 rounded transition-colors"
                  title={translations?.organizerRow?.edit || 'Edit'}
                >
                  <Edit size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 space-y-3">
            {/* Email */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600">
                <Mail size={16} />
                <span className="text-sm">Email:</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-sm font-medium truncate max-w-[150px]">{organizer.email}</span>
                <button
                  onClick={() => copyToClipboard(organizer.email, 'email')}
                  className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                  title={translations?.organizerRow?.copyEmail || 'Copy Email'}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>

            {/* Created At */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 size={16} />
                <span className="text-sm">Created:</span>
              </div>
              <span className="text-xs text-gray-500">
                {new Date(organizer.createdAt).toLocaleDateString()}
              </span>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mt-4">
              {/* Replace Auth Button */}
              <button
                onClick={() => setReplacingAuth(organizer)}
                className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm flex-1
                  bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
              >
                <KeyRound size={16} />
                <span>Replace Auth</span>
              </button>
              
              {/* Sync Button */}
              {onSyncNameClick && (
                <button
                  onClick={() => {
                    setSyncingOrganizerId(organizer.id);
                    onSyncNameClick(organizer.id);
                    setTimeout(() => setSyncingOrganizerId(null), 2000);
                  }}
                  disabled={syncingOrganizerId === organizer.id}
                  className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm flex-1
                    ${syncingOrganizerId === organizer.id 
                      ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                      : 'bg-primary-50 text-primary-600 hover:bg-primary-100'}`}
                  title={translations?.organizerRow?.syncName || 'Sync Name References'}
                >
                  <RefreshCw size={16} className={syncingOrganizerId === organizer.id ? 'animate-spin' : ''} />
                  <span>
                    {syncingOrganizerId === organizer.id
                      ? (translations?.organizerRow?.syncing || 'Syncing...')
                      : (translations?.organizerRow?.syncName || 'Sync Name References')}
                  </span>
                </button>
              )}
            </div>
          </div>
        </motion.div>
      ))}

      {/* Edit Modal */}
      {editing && (
        <EditOrganizerModal
          isOpen={!!editing}
          onClose={() => setEditing(null)}
          organizer={editing}
        />
      )}

      {/* Replace Auth Modal */}
      {replacingAuth && (
        <ReplaceAuthModal
          isOpen={!!replacingAuth}
          onClose={() => setReplacingAuth(null)}
          organizer={replacingAuth}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {isOpen && config && (
        <ConfirmDialog
          isOpen={isOpen}
          onClose={close}
          onConfirm={config.onConfirm}
          title={config.title}
          message={config.message}
          type={config.type}
          confirmText={translations?.organizersList?.confirmDelete || 'Delete'}
          cancelText={translations?.organizersList?.cancelDelete || 'Cancel'}
        />
      )}
    </div>
  );
};
